import { useEffect } from "react";
import * as Tone from "tone";
import { getPhraseMelodyNote, MELODY_PHRASES, ChordContext } from "./harmonyEngine";
import { SampleKit } from "./useSamples";

export interface MelodyConfig {
  enabled: boolean;
  midiMin: number;
  midiMax: number;
  guitarMode?: boolean;
  phrasePool?: number[]; // indices into MELODY_PHRASES; undefined = all phrases
}

// Guitar-appropriate register: warm mid-range of the nylon guitar sample set
const GUITAR_MIDI_MIN = 52; // E3
const GUITAR_MIDI_MAX = 71; // B4

// Durations longer than a quarter note decay into silence on guitar samples
const GUITAR_DUR_CAP: Record<string, string> = { "2n": "8n", "1n": "8n" };

export function useMelody(
  samples: SampleKit | null,
  chordContextRef: { current: ChordContext | null },
  configRef: { current: MelodyConfig },
  melodySamplerRef: { current: Tone.Sampler | null },
): void {
  useEffect(() => {
    if (!samples) return;

    const id = Tone.Transport.scheduleRepeat((time) => {
      const cfg = configRef.current;
      if (!cfg.enabled) return;

      const ctx = chordContextRef.current;
      if (!ctx) return;

      const sampler = melodySamplerRef.current;
      if (!sampler) return;

      const eighth = Tone.Time("8n").toSeconds();
      const midiMin = cfg.guitarMode ? GUITAR_MIDI_MIN : cfg.midiMin;
      const midiMax = cfg.guitarMode ? GUITAR_MIDI_MAX : cfg.midiMax;
      const capDur = (dur: string) => cfg.guitarMode ? (GUITAR_DUR_CAP[dur] ?? dur) : dur;

      const pool = cfg.phrasePool ?? MELODY_PHRASES.map((_, i) => i);
      const phraseIndex = pool[Math.floor(Math.random() * pool.length)];
      const phrase = MELODY_PHRASES[Math.min(phraseIndex, MELODY_PHRASES.length - 1)];
      const rootMidi = ctx.keyRootMidi + ctx.degree.semitones;

      for (const pn of phrase) {
        const note = getPhraseMelodyNote(pn.degreeIndex, rootMidi, ctx.degree.type, midiMin, midiMax);
        if (note) {
          sampler.triggerAttackRelease(note, capDur(pn.dur), time + eighth * pn.offset8n);
        }
      }
    }, "2m");

    return () => {
      Tone.Transport.clear(id);
    };
  }, [samples, chordContextRef, configRef, melodySamplerRef]);
}
